import { useEffect } from "react";

import { useTranslator } from "@eo-locale/react";

import { Box } from "@/storybook-components";

import { sanitizeHref } from "@/lib";
import { useAppSelector } from "@/redux/store/hooks";
import { roles } from "../lib/roles";

import UserAvatar from "../user-avatar";

const AuthLinks = () => {
  const { SiteAdmin, PlatformAdmin, HubAdmin, ProjectAdmin } = roles;
  const { translate } = useTranslator();

  const { profile } = useAppSelector((state) => state.user.registeredUser);
  const { id, roleName, login } = profile;
  const { homePageRevision } = useAppSelector(
    (state) => state.homePageRevision
  );
  const { settings } = useAppSelector((state) => state.settings);
  const { site } = useAppSelector((state) => state.site);
  const isSiteBlocked = site.blocked;
  const { features } = useAppSelector((state) => state.features);
  const isSiteOnboardingEnabled =
    features?.enableSiteOnboarding && features.enableSiteOnboarding.enabled;
  const { data } = homePageRevision?.sections?.sections[0] || {};
  const { signInText, signInUrl, signUpText, signUpUrl } =
    (Array.isArray(data) && data.length > 0 && data[0]) || {};

  const backgroundColor =
    homePageRevision?.sections?.sections[0].theme.mainNavbarBackgroundColor;
  const textColor =
    homePageRevision?.sections?.sections[0].theme.mainNavbarTextColor;

  useEffect(() => {
    // @ts-ignore
    if (!(id && roleName && typeof MetisMenu === "function")) return;

    // @ts-ignore
    // eslint-disable-next-line no-undef
    const dropdownMenu = new MetisMenu("#js-auth-links").on(
      "shown.metisMenu",
      function (event) {
        const shownElement = event.detail.shownElement as HTMLElement;
        const handleOutsideClick = (e: { target: unknown }) => {
          const target = e.target;
          if (!(target instanceof Element) || !shownElement.contains(target)) {
            dropdownMenu.hide(shownElement);
            window.removeEventListener("mousedown", handleOutsideClick);
          }
        };
        window.addEventListener("mousedown", handleOutsideClick);
      }
    );

    return () => {
      dropdownMenu.dispose();
    };
  }, [roleName, id]);

  const handleClick = async () => {
    const response = await fetch("/admin/api/auth/logout", {
      method: "DELETE"
    });

    if (response.ok) {
      const isAnAdmin =
        [SiteAdmin, PlatformAdmin, HubAdmin, ProjectAdmin].indexOf(roleName) !==
        -1;
      const showAdminLoginPage =
        isSiteBlocked && isAnAdmin && isSiteOnboardingEnabled;

      // Full navigation keeps logout redirects working under App and Pages.
      window.location.assign(showAdminLoginPage ? "/admin/login" : "/login");
    }
  };

  const generateProfileEditLink = () => {
    switch (roleName) {
      case "platform_admin":
        return "/platform/profile/edit";

      case "site_admin":
      case "hub_admin":
      case "project_admin":
        return `/admin/people/administrators/${id}`;

      case "participant":
        return "/profile";

      default:
        return "/profile";
    }
  };

  const isAdminUIDashboardEnabled = () =>
    features?.["switchToAdminUi"]?.enabled &&
    features?.["reportingDashboardV2"]?.enabled
      ? "/adm"
      : "/admin/dashboard";

  if (id && roleName) {
    return (
      <div className="auth-links">
        <ul
          className="metismenu auth-links__list"
          id="js-auth-links"
          data-testid="user-profile-dropdown"
        >
          <li className="auth-links__list__list-item">
            <a
              href="#"
              aria-expanded="false"
              className="metismenu profile-info"
              data-testid="participant-header-profile-dropdown-trigger"
            >
              <UserAvatar />
              <span>{login}</span>
            </a>
            <Box
              as="ul"
              className="auth-links__list__list-item__child mm-collapse"
              color={textColor}
              backgroundColor={backgroundColor}
            >
              <li className="auth-links__list__list-item">
                <a
                  href={generateProfileEditLink()}
                  className="metismenu auth-links__list__list-item__link"
                  data-testid="participant-header-my-account-link"
                >
                  {translate("participantHeader.myAccount")}
                </a>
              </li>
              {[
                "platform_admin",
                "site_admin",
                "hub_admin",
                "project_admin"
              ].indexOf(roleName) > -1 && (
                <li className="auth-links__list__list-item">
                  <a
                    href="/admin/projects"
                    aria-expanded="false"
                    className="metismenu auth-links__list__list-item__link"
                    data-testid="participant-header-projects-link"
                  >
                    {translate("participantHeader.projects")}
                  </a>
                </li>
              )}
              {[
                "platform_admin",
                "site_admin",
                "hub_admin",
                "project_admin"
              ].indexOf(roleName) > -1 && (
                <li className="auth-links__list__list-item">
                  <a
                    href={isAdminUIDashboardEnabled()}
                    aria-expanded="false"
                    className="metismenu auth-links__list__list-item__link"
                    data-testid="participant-header-dashboard-link"
                  >
                    {translate("participantHeader.dashboard")}
                  </a>
                </li>
              )}
              {["platform_admin", "site_admin"].indexOf(roleName) > -1 && (
                <li className="auth-links__list__list-item">
                  <a
                    href="/admin/sites/editors/home-page"
                    aria-expanded="false"
                    className="metismenu auth-links__list__list-item__link"
                    data-testid="participant-header-appearance-link"
                  >
                    {translate("participantHeader.appearance")}
                  </a>
                </li>
              )}
              <li className="auth-links__list__list-item">
                <a
                  href="#!"
                  onClick={handleClick}
                  className="auth-links__list__list-item__link"
                  data-testid="participant-header-logout-link"
                >
                  {translate("participantHeader.logout")}
                </a>
              </li>
            </Box>
          </li>
        </ul>
      </div>
    );
  } else {
    return (
      <ul className="auth-links__list">
        <li className="auth-links__list__list-item">
          <a
            href={sanitizeHref(signInUrl)}
            className="auth-links__list__list-item__link"
            data-testid="participant-header-sign-in-link"
          >
            {signInText || translate("participantHeader.signIn")}
          </a>
        </li>
        {!settings.disableSignup && (
          <li className="auth-links__list__list-item">
            <a
              href={sanitizeHref(signUpUrl)}
              className="auth-links__list__list-item__link"
              data-testid="participant-header-sign-up-link"
            >
              {signUpText || translate("participantHeader.signUp")}
            </a>
          </li>
        )}
      </ul>
    );
  }
};

export default AuthLinks;
